import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { WebsiteLayout } from '@/components/website/WebsiteLayout';
import { Phone, Mail, MapPin, Send, MessageCircle, Clock, ArrowRight, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { el.classList.add('visible'); obs.unobserve(el); } }, { threshold: 0.12 });
    obs.observe(el); return () => obs.disconnect();
  }, []);
  return ref;
};
const Reveal = ({ children, className = '', dir = 'up', delay = 0 }) => {
  const ref = useReveal();
  const cls = dir === 'left' ? 'reveal-left' : dir === 'right' ? 'reveal-right' : 'reveal';
  return <div ref={ref} className={`${cls} ${delay ? `stagger-${delay}` : ''} ${className}`}>{children}</div>;
};

export default function ContactPage() {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', email: '', property_interest: '', location_interest: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    api.get('/public/website-settings').then(r => setSettings(r.data)).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) { toast.error('Name aur phone number zaroori hai'); return; }
    setSubmitting(true);
    try {
      await api.post('/public/enquiry', form);
      toast.success('Enquiry submit ho gayi! Hum jaldi contact karenge.');
      setForm({ name: '', phone: '', email: '', property_interest: '', location_interest: '', message: '' });
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 5000);
    } catch {
      toast.error('Submit nahi hua. Please retry karein.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WebsiteLayout>
      <Helmet>
        <title>{settings?.company_name ? `Contact Us | ${settings.company_name}` : 'Contact Us | Ganesa Homestate Developers'}</title>
        <meta name="description" content={`${settings?.company_name || 'Ganesa Homestate Developers'} se contact karein property enquiry, site visit, aur investment ke liye. Call ${settings?.phone || ''}`} />
      </Helmet>

      {/* Hero */}
      <section className="relative bg-[#0A0A0F] py-12 sm:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#DAA520]/5 via-transparent to-[#DAA520]/3" />
        <div className="absolute top-10 right-20 w-60 h-60 bg-[#DAA520]/5 rounded-full blur-3xl animate-float-slow hidden lg:block" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-10 h-[2px] bg-[#DAA520]" />
            <span className="text-[#DAA520] text-xs sm:text-sm font-bold uppercase tracking-[0.2em]">Contact Us</span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-extrabold text-white leading-[1.1]">
            Get in <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#DAA520] to-[#FFD700]">Touch</span>
          </h1>
          <p className="text-slate-400 text-sm sm:text-base mt-3 max-w-lg">Koi bhi sawaal ho - property, investment, ya site visit ke liye. Hum hamesha available hain!</p>
        </div>
      </section>

      {/* Quick Contact Cards */}
      <section className="relative -mt-6 sm:-mt-8 z-10 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {[
              { icon: Phone, label: 'Call Us', value: settings?.phone || '+91 9876543210', href: `tel:${settings?.phone}`, color: 'from-emerald-500 to-emerald-700' },
              { icon: MessageCircle, label: 'WhatsApp', value: 'Quick Response', href: `https://wa.me/${(settings?.whatsapp || settings?.phone || '').replace(/[^0-9]/g, '')}`, color: 'from-green-500 to-green-700' },
              { icon: Mail, label: 'Email', value: settings?.email || 'info@ganesa.com', href: `mailto:${settings?.email}`, color: 'from-blue-500 to-blue-700' },
              { icon: Clock, label: 'Working Hours', value: 'Mon-Sat: 9AM-7PM', href: null, color: 'from-purple-500 to-purple-700' },
            ].map((c, i) => (
              <Reveal key={i} delay={i + 1}>
                {c.href ? (
                  <a href={c.href} target={c.href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer"
                    className="group bg-white rounded-2xl p-4 sm:p-5 shadow-lg hover:shadow-2xl transition-all duration-500 border border-slate-100 hover-lift block">
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br ${c.color} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-md`}>
                      <c.icon size={18} className="text-white" />
                    </div>
                    <div className="text-[10px] sm:text-xs text-slate-400 uppercase tracking-wider font-semibold mb-0.5">{c.label}</div>
                    <div className="text-xs sm:text-sm font-bold text-[#0A0A0F] group-hover:text-[#DAA520] transition-colors truncate">{c.value}</div>
                  </a>
                ) : (
                  <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-lg border border-slate-100">
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br ${c.color} flex items-center justify-center mb-3 shadow-md`}>
                      <c.icon size={18} className="text-white" />
                    </div>
                    <div className="text-[10px] sm:text-xs text-slate-400 uppercase tracking-wider font-semibold mb-0.5">{c.label}</div>
                    <div className="text-xs sm:text-sm font-bold text-[#0A0A0F]">{c.value}</div>
                  </div>
                )}
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-5 gap-8 sm:gap-12">
            {/* Contact Info */}
            <Reveal dir="left" className="lg:col-span-2">
              <div className="bg-[#0A0A0F] rounded-3xl p-6 sm:p-8 relative overflow-hidden sticky top-24">
                <div className="absolute inset-0 bg-gradient-to-br from-[#DAA520]/5 via-transparent to-[#DAA520]/3" />
                <div className="relative">
                  <h2 className="text-xl sm:text-2xl font-extrabold text-white mb-6">Contact Information</h2>
                  <div className="space-y-5">
                    <a href={`tel:${settings?.phone}`} className="flex items-start gap-4 group">
                      <div className="w-12 h-12 rounded-xl bg-[#DAA520]/10 flex items-center justify-center shrink-0 group-hover:bg-[#DAA520] transition-all duration-300 group-hover:scale-110">
                        <Phone size={20} className="text-[#DAA520] group-hover:text-[#0A0A0F] transition-colors" />
                      </div>
                      <div>
                        <div className="text-xs text-slate-500 mb-0.5">Phone</div>
                        <div className="text-white font-bold text-sm sm:text-base group-hover:text-[#DAA520] transition-colors">{settings?.phone || '+91 9876543210'}</div>
                      </div>
                    </a>
                    <a href={`mailto:${settings?.email}`} className="flex items-start gap-4 group">
                      <div className="w-12 h-12 rounded-xl bg-[#DAA520]/10 flex items-center justify-center shrink-0 group-hover:bg-[#DAA520] transition-all duration-300 group-hover:scale-110">
                        <Mail size={20} className="text-[#DAA520] group-hover:text-[#0A0A0F] transition-colors" />
                      </div>
                      <div>
                        <div className="text-xs text-slate-500 mb-0.5">Email</div>
                        <div className="text-white font-bold text-sm sm:text-base group-hover:text-[#DAA520] transition-colors">{settings?.email || 'info@ganesahomestate.com'}</div>
                      </div>
                    </a>
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-[#DAA520]/10 flex items-center justify-center shrink-0">
                        <MapPin size={20} className="text-[#DAA520]" />
                      </div>
                      <div>
                        <div className="text-xs text-slate-500 mb-0.5">Office Address</div>
                        <div className="text-white font-bold text-sm sm:text-base">{settings?.address || 'Office Address'}</div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-8 pt-6 border-t border-[#1A1A25]">
                    <a href={`https://wa.me/${(settings?.whatsapp || settings?.phone || '').replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full py-3.5 bg-[#25D366] text-white font-bold rounded-xl hover:bg-[#1fb855] transition-all hover:shadow-[0_8px_30px_rgba(37,211,102,0.3)] text-sm active:scale-95">
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
                      WhatsApp Pe Message Karein
                    </a>
                  </div>
                </div>
              </div>
            </Reveal>

            {/* Enquiry Form */}
            <Reveal dir="right" className="lg:col-span-3">
              <div className="bg-white rounded-3xl p-6 sm:p-10 shadow-xl border border-slate-100 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-[#DAA520] via-[#FFD700] to-[#DAA520]" />
                
                {submitted ? (
                  <div className="text-center py-10 sm:py-16">
                    <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
                      <CheckCircle size={40} className="text-emerald-600" />
                    </div>
                    <h3 className="text-xl sm:text-2xl font-extrabold text-[#0A0A0F] mb-2">Enquiry Submit Ho Gayi!</h3>
                    <p className="text-slate-500 text-sm">Hamari team 24 hours me aapko contact karegi. Dhanyavaad!</p>
                  </div>
                ) : (
                  <>
                    <h2 className="text-xl sm:text-2xl font-extrabold text-[#0A0A0F] mb-1">Send Us a Message</h2>
                    <p className="text-slate-500 text-xs sm:text-sm mb-6 sm:mb-8">Form fill karein aur hamari team 24 hours me respond karegi.</p>
                    <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5" data-testid="enquiry-form">
                      <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
                        <div>
                          <label className="text-xs sm:text-sm font-bold text-[#0A0A0F] mb-1.5 block">Full Name *</label>
                          <input type="text" data-testid="enquiry-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required
                            className="w-full px-4 py-3 sm:py-3.5 rounded-xl border border-slate-200 focus:outline-none focus:border-[#DAA520] focus:ring-2 focus:ring-[#DAA520]/20 text-sm transition-all hover:border-slate-300" placeholder="Aapka naam" />
                        </div>
                        <div>
                          <label className="text-xs sm:text-sm font-bold text-[#0A0A0F] mb-1.5 block">Phone Number *</label>
                          <input type="tel" data-testid="enquiry-phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} required
                            className="w-full px-4 py-3 sm:py-3.5 rounded-xl border border-slate-200 focus:outline-none focus:border-[#DAA520] focus:ring-2 focus:ring-[#DAA520]/20 text-sm transition-all hover:border-slate-300" placeholder="+91 98765 43210" />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs sm:text-sm font-bold text-[#0A0A0F] mb-1.5 block">Email</label>
                        <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                          className="w-full px-4 py-3 sm:py-3.5 rounded-xl border border-slate-200 focus:outline-none focus:border-[#DAA520] focus:ring-2 focus:ring-[#DAA520]/20 text-sm transition-all hover:border-slate-300" placeholder="your@email.com" />
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
                        <div>
                          <label className="text-xs sm:text-sm font-bold text-[#0A0A0F] mb-1.5 block">Property Interest</label>
                          <select value={form.property_interest} onChange={e => setForm({ ...form, property_interest: e.target.value })}
                            className="w-full px-4 py-3 sm:py-3.5 rounded-xl border border-slate-200 focus:outline-none focus:border-[#DAA520] bg-white text-sm transition-all hover:border-slate-300">
                            <option value="">Select Type</option>
                            <option>Plots</option><option>Flats</option><option>Villas</option><option>Commercial</option><option>Farm House</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-xs sm:text-sm font-bold text-[#0A0A0F] mb-1.5 block">Preferred Location</label>
                          <input type="text" value={form.location_interest} onChange={e => setForm({ ...form, location_interest: e.target.value })}
                            className="w-full px-4 py-3 sm:py-3.5 rounded-xl border border-slate-200 focus:outline-none focus:border-[#DAA520] focus:ring-2 focus:ring-[#DAA520]/20 text-sm transition-all hover:border-slate-300" placeholder="e.g., Khatushyam Ji" />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs sm:text-sm font-bold text-[#0A0A0F] mb-1.5 block">Message</label>
                        <textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} rows={4}
                          className="w-full px-4 py-3 sm:py-3.5 rounded-xl border border-slate-200 focus:outline-none focus:border-[#DAA520] focus:ring-2 focus:ring-[#DAA520]/20 resize-none text-sm transition-all hover:border-slate-300" placeholder="Aapki requirements batayein..." />
                      </div>
                      <button type="submit" disabled={submitting} data-testid="enquiry-submit"
                        className="w-full py-4 bg-gradient-to-r from-[#DAA520] to-[#B8860B] text-[#0A0A0F] font-bold rounded-xl hover:shadow-[0_8px_40px_rgba(201,168,76,0.4)] transition-all flex items-center justify-center gap-2 disabled:opacity-50 text-sm active:scale-[0.98] hover:scale-[1.01]">
                        <Send size={16} /> {submitting ? 'Submitting...' : 'Submit Enquiry'}
                      </button>
                    </form>
                  </>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Google Maps */}
      {settings?.google_maps_embed && (
        <Reveal>
          <section className="pb-12 sm:pb-16" data-testid="google-maps-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
              <div className="rounded-3xl overflow-hidden shadow-2xl border border-slate-100"
                dangerouslySetInnerHTML={{ __html: settings.google_maps_embed.replace(/<iframe/g, '<iframe style="width:100%;height:400px;border:0;" loading="lazy" referrerpolicy="no-referrer-when-downgrade"') }} />
            </div>
          </section>
        </Reveal>
      )}
    </WebsiteLayout>
  );
}
